import { memo } from "react";
import { FleetTireSetEditFormValues } from "services/api/fleet/tires/types";
import { formatKm, getDimensionSet, getTireType, getTireManufacturers } from "services/api/fleet/tires/helpers";
import Badge from "ui/components/Badge/Badge";
import { transformDateToLocalDateString } from "helpers";
import { MountFormValues } from "../types";

interface ResolvedMountNames {
  targetVehicle?: {
    name?: string | null;
    registration?: string | null;
    image?: string | null;
  };
  oldSetOnVehicle?: {
    code?: string | null;
    dimension?: string | null;
    type?: string | null;
    manufacturer?: string | null;
  };
  oldSetWarehouseName?: string | null;
}

interface MountSummaryProps {
  currentSet: FleetTireSetEditFormValues;
  formValues: MountFormValues;
  resolvedNames: ResolvedMountNames;
}

const InfoRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex justify-between items-start py-2.5">
    <span className="text-sm text-slate-500 min-w-[160px]">{label}</span>
    <div className="text-sm font-medium text-slate-900 text-right">{children}</div>
  </div>
);

const MountSummary: React.FC<MountSummaryProps> = ({ currentSet, formValues, resolvedNames }) => {
  const { targetVehicle, oldSetOnVehicle, oldSetWarehouseName } = resolvedNames;
  const tireType = getTireType(currentSet.tires);
  const dimension = getDimensionSet(currentSet.tires);
  const manufacturers = getTireManufacturers(currentSet.tires);
  const dateStr = formValues.transferDate ? (transformDateToLocalDateString(formValues.transferDate) ?? "-") : "-";

  return (
    <div className="flex items-center justify-center px-4 min-h-[70vh] 2xl:min-h-[1000px]">
      <div className="w-full max-w-2xl">
        <h1 className="text-2xl font-bold text-slate-900 text-center mb-1 mb-2">Potvrda montaže</h1>
        <p className="text-center text-md text-slate-500 mb-8">Provjerite podatke prije izvršenja montaže.</p>

        <div className="space-y-4">
          {/* Set guma koji se montira */}
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
            <div className="px-5 pt-4 pb-1">
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Set guma koji se montira</div>
              <div className="divide-y divide-slate-100">
                <InfoRow label="Kod seta"><span className="font-semibold">{currentSet.code}</span></InfoRow>
                <InfoRow label="Dimenzija">{dimension || "-"}</InfoRow>
                <InfoRow label="Tip gume">{tireType?.name ?? "-"}</InfoRow>
                <InfoRow label="Proizvođač">{manufacturers || "-"}</InfoRow>
                <InfoRow label="Broj guma">{currentSet.tires?.length ?? 0}</InfoRow>
                <InfoRow label="Datum montaže">{dateStr}</InfoRow>
                <InfoRow label="Kilometraža kod montaže">{formatKm(formValues.kmTraveled)}</InfoRow>
              </div>
            </div>
          </div>

          {/* Montaža na vozilo */}
          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
            <div className="px-5 pt-4 pb-1">
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Montaža na vozilo</div>
              <div className="divide-y divide-slate-100">
                <InfoRow label="Montira se na vozilo">
                  <div className="flex items-center gap-2">
                    {targetVehicle?.image && (
                      <img src={targetVehicle.image} alt="" className="h-6 w-6 object-contain rounded-full border border-slate-200" />
                    )}
                    <span className="font-semibold">{targetVehicle?.name ?? "-"}</span>
                  </div>
                </InfoRow>
                {targetVehicle?.registration && (
                  <InfoRow label="Registracija">{targetVehicle.registration}</InfoRow>
                )}
              </div>
            </div>

            {/* Demontaža starog seta */}
            {oldSetOnVehicle?.code && (
              <div className="px-5 pt-3 pb-1 border-t border-slate-200 bg-amber-50/50">
                <div className="text-xs font-semibold text-amber-600 uppercase tracking-wider mb-2">Demontaža starog seta s vozila</div>
                <div className="divide-y divide-slate-100">
                  <InfoRow label="Stari set">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{oldSetOnVehicle.code}</span>
                      <Badge text="Demontira se" variant="warning" height="small" />
                    </div>
                  </InfoRow>
                  {oldSetOnVehicle.dimension && <InfoRow label="Dimenzija">{oldSetOnVehicle.dimension}</InfoRow>}
                  {oldSetOnVehicle.type && <InfoRow label="Tip gume">{oldSetOnVehicle.type}</InfoRow>}
                  {oldSetOnVehicle.manufacturer && <InfoRow label="Proizvođač">{oldSetOnVehicle.manufacturer}</InfoRow>}
                  <InfoRow label="Sprema se na skladište">
                    <span className="font-semibold">{oldSetWarehouseName ?? "-"}</span>
                  </InfoRow>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(MountSummary);
